import React from 'react';
// Message-related helpers for WhatsAppManager, MessagePanel, and MessageItem
export function getInitials(name: string, phone: string) {
  if (name) {
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return phone.slice(-2).toUpperCase();
}

export function getDisplayContact(pic_contact: { email?: string; whatsapp?: string; slack?: string; ms_team?: string; line?: string; } | string): string {
  // Handle legacy string format
  if (typeof pic_contact === 'string') {
    return pic_contact;
  }
  
  // Handle new JSON format - return the first available contact
  if (pic_contact) {
    return pic_contact.whatsapp || pic_contact.email || pic_contact.slack || pic_contact.ms_team || pic_contact.line || '';
  }
  
  return '';
}

export function getContactForChannel(
  pic_contact: { [k: string]: string | undefined } | string | undefined,
  channel: string
): string {
  if (!pic_contact) return '';
  if (typeof pic_contact === 'string') return pic_contact;
  return pic_contact[channel] || '';
}

export function timeAgo(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)} days ago`;
  return date.toLocaleDateString();
}

export function getFileType(url: string) {
  if (!url) return '';
  if (url.endsWith('.pdf')) return 'pdf';
  if (url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png')) return 'image';
  return 'file';
}

export function getFileName(url: string) {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const filename = pathname.split('/').pop() || '';
    return decodeURIComponent(filename.split('?')[0]);
  } catch {
    return decodeURIComponent(url.split('/').pop()?.split('?')[0] || url);
  }
}

export function formatMessageText(text: string): React.ReactNode {
  if (!text) return null;
  
  // Split text by newlines first to handle line breaks safely
  const lines = text.split('\n');
  
  return React.createElement('span', null, 
    lines.map((line: string, lineIndex: number) => {
      // Process each line for formatting
      const parts: React.ReactNode[] = [];
      let remaining = line;
      let partIndex = 0;
      
      while (remaining.length > 0) {
        // Check for code blocks first (```text```)
        const codeMatch = remaining.match(/^```([^`]+)```/);
        if (codeMatch) {
          parts.push(
            React.createElement('code', {
              key: `code-${lineIndex}-${partIndex}`,
              className: 'bg-slate-200 px-1 py-0.5 rounded text-sm font-mono'
            }, codeMatch[1])
          );
          remaining = remaining.slice(codeMatch[0].length);
          partIndex++;
          continue;
        }
        
        // Check for bold text (**text**)
        const boldMatch = remaining.match(/^\*([^*]+)\*/);
        if (boldMatch) {
          parts.push(
            React.createElement('strong', {
              key: `bold-${lineIndex}-${partIndex}`
            }, boldMatch[1])
          );
          remaining = remaining.slice(boldMatch[0].length);
          partIndex++;
          continue;
        }
        
        // Check for italic text (_text_)
        const italicMatch = remaining.match(/^_([^_]+)_/);
        if (italicMatch) {
          parts.push(
            React.createElement('em', {
              key: `italic-${lineIndex}-${partIndex}`
            }, italicMatch[1])
          );
          remaining = remaining.slice(italicMatch[0].length);
          partIndex++;
          continue;
        }
        
        // Check for strikethrough text (~text~)
        const strikeMatch = remaining.match(/^~([^~]+)~/);
        if (strikeMatch) {
          parts.push(
            React.createElement('del', {
              key: `strike-${lineIndex}-${partIndex}`
            }, strikeMatch[1])
          );
          remaining = remaining.slice(strikeMatch[0].length);
          partIndex++;
          continue;
        }
        
        // No match found, add the next character as plain text
        parts.push(remaining[0]);
        remaining = remaining.slice(1);
      }
      
      return React.createElement(React.Fragment, { key: lineIndex },
        ...parts,
        lineIndex < lines.length - 1 ? React.createElement('br') : null
      );
    })
  );
}

export function extractMessageContent(msg: any) {
  let type = msg.message_type;
  let text = msg.content || '';
  let htmlContent: string | null = null; // For email HTML content
  let documentUrl = '';
  let documentName = '';
  let imageUrl = '';
  let previewUrl = '';
  let templateName = '';
  let infoLabel = '';
  let infoDesc = '';
  let emailAttachments: Array<{ filename: string; content: string; content_type?: string }> = []; // For email attachments
  let emailSubject: string | null = null; // For email subject line
  let parsed: any = null;
  if (typeof msg.content === 'string' && msg.content.trim().startsWith('{')) {
    try { parsed = JSON.parse(msg.content); } catch { }
  }
  if (!parsed && msg.payload_json) { parsed = msg.payload_json; }
  
  // Handle email messages with HTML content and attachments
  if (msg.channel === 'email') {
    // Extract subject from payload_json
    if (parsed) {
      // For outbound emails, subject is in payload_json.subject
      if (parsed.subject) {
        emailSubject = parsed.subject;
      }
      // For inbound emails, subject might be in payload_json.headers.Subject or payload_json.subject
      else if (parsed.headers && parsed.headers.Subject) {
        emailSubject = parsed.headers.Subject;
      }
    }
    
    // Extract attachments from payload_json (for sent emails)
    if (parsed && parsed.attachments && Array.isArray(parsed.attachments)) {
      emailAttachments = parsed.attachments.map((att: any) => ({
        filename: att.filename || 'attachment',
        content: att.content || '',
        content_type: att.content_type
      }));
    }
    
    // For inbound emails, prioritize msg.content (clean content without quoted messages)
    // For outbound emails, check payload_json for body_html first
    if (msg.direction === 'inbound') {
      // For inbound emails, use msg.content (which is already clean, without quoted messages)
      // Check if content is HTML
      if (msg.message_type === 'html' && msg.content) {
        htmlContent = msg.content;
      }
      // Check if content itself looks like HTML (contains HTML tags)
      else if (msg.content && /<[a-z][\s\S]*>/i.test(msg.content)) {
        htmlContent = msg.content;
      }
      // If no HTML content found in msg.content, fall back to original_body_html only if msg.content is empty
      if (!htmlContent && !msg.content && parsed && parsed.original_body_html) {
        htmlContent = parsed.original_body_html;
      }
      // Use msg.content as text if it's not HTML
      if (!htmlContent && msg.content) {
        text = msg.content;
      }
      // Only use original_body_text if msg.content is empty
      if (!text && !msg.content && parsed && parsed.original_body_text) {
        text = parsed.original_body_text;
      }
    } else {
      // For outbound emails, check payload_json for body_html first
      if (parsed && parsed.body_html) {
        htmlContent = parsed.body_html;
        // Use body_text as fallback for text if HTML is not available
        if (!text && parsed.body_text) {
          text = parsed.body_text;
        }
      }
      // Also check if message_type is 'html' and content contains HTML
      else if (msg.message_type === 'html' && msg.content) {
        htmlContent = msg.content;
      }
      // Check if content itself looks like HTML (contains HTML tags)
      else if (msg.content && /<[a-z][\s\S]*>/i.test(msg.content)) {
        htmlContent = msg.content;
      }
    }
  }
  
  if (parsed) {
    if (parsed.type === 'text' && parsed.text) {
      type = 'text';
      text = parsed.text.body || '';
    } else if (parsed.type === 'document' && parsed.document) {
      type = 'document';
      documentUrl = parsed.document.url || '';
      documentName = parsed.document.filename || '';
    } else if (parsed.type === 'image' && parsed.image) {
      type = 'image';
      imageUrl = parsed.image.url || '';
      documentName = parsed.image.filename || '';
    } else if (parsed.type === 'template' && parsed.template) {
      type = 'template';
      templateName = parsed.template.name || '';
      text = msg.content || '';
      if (!text) { text = `Template message: ${templateName}`; }
    } else if (parsed.type === 'info' || parsed.type === 'system') {
      type = 'info';
      infoLabel = parsed.label || 'Document';
      infoDesc = parsed.desc || parsed.body || 'Processing document from WhatsApp servers...';
    } else if (parsed.text && typeof parsed.text === 'string') {
      type = 'text';
      text = parsed.text;
    }
  }
  if (!documentUrl && msg.media_urls && msg.media_urls.length > 0) {
    const url = msg.media_urls[0];
    if (getFileType(url) === 'pdf') {
      type = 'document';
      documentUrl = url;
      documentName = getFileName(url);
    } else if (getFileType(url) === 'image') {
      type = 'image';
      imageUrl = url;
    }
  }
  if (msg.backup_media_urls && msg.backup_media_urls.length > 0) {
    previewUrl = msg.backup_media_urls[0];
    if (!imageUrl && type === 'image') imageUrl = previewUrl;
    if (!documentUrl && type === 'document') documentUrl = previewUrl;
  }
  if (!documentName && documentUrl) {
    documentName = getFileName(documentUrl);
  }
  return { type, text, htmlContent, documentUrl, documentName, imageUrl, previewUrl, templateName, infoLabel, infoDesc, emailAttachments, emailSubject };
} 